import { IPublicClientApplication } from '@azure/msal-browser';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { acquireApiAccessTokenAsync } from '../auth/AuthService';
import { trackEvent, trackServiceException } from '../../app/telemetry/appInsights';

export class FatalConnectionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'FatalConnectionError';
    }
}

export interface SseEvent {
    type: string;
    content?: string;
    data?: any;
    threadId?: string;
    runId?: string;
    messageId?: string;
    timestamp?: string;
    agentStatus?: string;
    sender?: string;
    isChunk?: boolean;
    chunkIndex?: number;
    isReasoning?: boolean;
    error?: string;
    message?: string;
    status?: string;
    channels?: string[];
    title?: string;
    citations?: unknown;
}

export interface SseConnectionConfig {
    endpoint: string;
    threadId: string;
    chatId: string;
    channels: string[];
    onMessage: (event: SseEvent) => void;
    onError: (error: Error) => void;
    onReconnect?: () => void;
}

export class SseConnectionManager {
    private readonly msalInstance: IPublicClientApplication;
    private activeConnections = new Map<string, AbortController>();
    private pendingConnections = new Map<string, Promise<void>>();
    private reconnectAttempts = new Map<string, number>();

    private readonly MAX_RECONNECT_ATTEMPTS = 5;
    private readonly RECONNECT_BASE_INTERVAL = 3000;

    constructor(msalInstance: IPublicClientApplication) {
        this.msalInstance = msalInstance;
    }

    connect(config: SseConnectionConfig): Promise<void> {
        const connectionKey = this.buildConnectionKey(config);

        // Deduplicate in-flight connects
        const pending = this.pendingConnections.get(connectionKey);
        if (pending) return pending;

        const promise = this.connectInternal(config, connectionKey).finally(() => {
            this.pendingConnections.delete(connectionKey);
        });

        this.pendingConnections.set(connectionKey, promise);
        return promise;
    }

    private async connectInternal(config: SseConnectionConfig, connectionKey: string): Promise<void> {
        // Guard against duplicate active connections
        if (this.activeConnections.has(connectionKey)) return;

        const abortController = new AbortController();
        this.activeConnections.set(connectionKey, abortController);

        // Build URL with query params
        const url = new URL('/events/stream', config.endpoint);
        url.searchParams.set('threadId', config.threadId);
        url.searchParams.set('channelTypes', config.channels.join(','));

        // Fetch token synchronously before fetchEventSource (headers must be sync)
        let token: string;
        try {
            token = await this.getAccessToken();
        } catch (error) {
            trackServiceException(error, {
                source: 'SseConnectionManager.getAccessToken',
                operation: 'connect',
                endpoint: url.pathname,
                sessionId: config.chatId,
                isFatal: true,
            }, {
                channels: config.channels.join(','),
            });
            this.activeConnections.delete(connectionKey);
            throw error;
        }

        const apimKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
        const subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';

        try {
            await fetchEventSource(url.toString(), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'text/event-stream',
                    'Cache-Control': 'no-cache',
                    [subKeyHeader]: apimKey,
                },
                signal: abortController.signal,
                openWhenHidden: true,

                onopen: async (response) => {
                    if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
                        this.reconnectAttempts.set(connectionKey, 0);
                        return;
                    }

                    // Fatal: 4xx errors (except 429 rate limit) — do not retry
                    if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                        throw new FatalConnectionError(
                            `SSE connection rejected: ${response.status} ${response.statusText}`,
                        );
                    }

                    throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
                },

                onmessage: (msg) => {
                    if (!msg.data) return;
                    try {
                        const event = JSON.parse(msg.data) as SseEvent;
                        this.reconnectAttempts.set(connectionKey, 0);
                        config.onMessage(event);
                    } catch (e) {
                        console.warn('Failed to parse SSE message:', msg.data, e);
                        trackServiceException(e, {
                            source: 'SseConnectionManager.onmessage.parse',
                            operation: 'parse_message',
                            endpoint: url.pathname,
                            sessionId: config.chatId,
                            isFatal: false,
                        }, {
                            eventType: msg.event ?? '',
                            dataLength: msg.data.length,
                        });
                    }
                },

                onerror: (err) => {
                    if (err instanceof FatalConnectionError) {
                        trackServiceException(err, {
                            source: 'SseConnectionManager.onerror',
                            operation: 'stream',
                            endpoint: url.pathname,
                            sessionId: config.chatId,
                            retryAttempt: this.reconnectAttempts.get(connectionKey) ?? 0,
                            isFatal: true,
                        });
                        config.onError(err);
                        throw err; // stop retrying
                    }

                    config.onError(err instanceof Error ? err : new Error(String(err)));

                    const attempts = (this.reconnectAttempts.get(connectionKey) ?? 0) + 1;
                    this.reconnectAttempts.set(connectionKey, attempts);

                    trackServiceException(err, {
                        source: 'SseConnectionManager.onerror',
                        operation: 'stream',
                        endpoint: url.pathname,
                        sessionId: config.chatId,
                        retryAttempt: attempts,
                        isFatal: false,
                    });

                    if (attempts >= this.MAX_RECONNECT_ATTEMPTS) {
                        const fatal = new FatalConnectionError('Max reconnection attempts exceeded');
                        trackServiceException(fatal, {
                            source: 'SseConnectionManager.maxReconnectExceeded',
                            operation: 'stream',
                            endpoint: url.pathname,
                            sessionId: config.chatId,
                            retryAttempt: attempts,
                            isFatal: true,
                        });
                        config.onError(fatal);
                        throw fatal; // stop retrying
                    }

                    config.onReconnect?.();

                    // Exponential backoff: 3s, 6s, 12s, 24s, 48s
                    return this.RECONNECT_BASE_INTERVAL * Math.pow(2, Math.min(attempts - 1, 4));
                },
            });
        } finally {
            if (abortController.signal.aborted) {
                trackEvent('sse.disconnected', {
                    source: 'SseConnectionManager.connectInternal.finally',
                    sessionId: config.chatId,
                    endpoint: url.pathname,
                    reason: 'aborted',
                });
            }
            this.activeConnections.delete(connectionKey);
            this.reconnectAttempts.delete(connectionKey);
        }
    }

    disconnect(connectionKey: string): void {
        const controller = this.activeConnections.get(connectionKey);
        if (controller) {
            controller.abort();
            this.activeConnections.delete(connectionKey);
            this.reconnectAttempts.delete(connectionKey);
        }
    }

    disconnectAll(): void {
        for (const controller of this.activeConnections.values()) {
            controller.abort();
        }
        this.activeConnections.clear();
        this.reconnectAttempts.clear();
        this.pendingConnections.clear();
    }

    buildConnectionKey(config: Pick<SseConnectionConfig, 'chatId' | 'channels'>): string {
        return `${config.chatId}-${config.channels.join(',')}`;
    }

    private async getAccessToken(): Promise<string> {
        let account = this.msalInstance.getActiveAccount();
        if (!account) {
            account = this.msalInstance.getActiveAccount();
            if (!account) {
                const accounts = this.msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    this.msalInstance.setActiveAccount(accounts[0]);
                } else {
                    throw new Error('Authentication failed: no active account');
                }
            }
        }
        const token = await acquireApiAccessTokenAsync({
            msalInstance: this.msalInstance,
        });
        if (!token) throw new Error('Authentication failed: empty token');
        return token;
    }
}
